import React from 'react';
import { GovIcon } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useEPeticeSeznamContext } from '@gov-nx/module/page';
import {
	Button,
	Dialog,
	DSGovFlex,
	FilterParameterList,
	FormDatePicker,
	FormSearch,
	FormWidthElementLayout,
	HeaderLayout,
	LayoutGap,
} from '@gov-nx/ui/web';

export interface EPetitionFilterProps {
	code: PageCode;
}

export const EPetitionFilter = ({ code }: EPetitionFilterProps) => {
	const { t } = useLocale(code);
	const {
		controls,
		filterParams,
		resetFilter,
		setControls,
		formDefinition,
		onSubmit,
		filter,
	} = useEPeticeSeznamContext();

	return (
		<GovFormWeb
			formDefinition={formDefinition}
			onSubmit={onSubmit}>
			<>
				<HeaderLayout
					filterParams={
						<FilterParameterList
							items={filterParams}
							onReset={() => resetFilter()}
						/>
					}
					className="flex justify-between gap-(--templates-margin-m)">
					<FormWidthElementLayout
						type="l"
						className="relative">
						<FormSearch
							input={{
								field: {
									name: 'text',
									isDisabled: controls.processLoading,
									placeholder: t('filtr.placeholder.vyhledat-epetici'),
								},
								label: {
									children: t('filtr.pole.wcag.vyhledat-epetici'),
								},
							}}
							button={{
								'aria-label': t('akce.wcag.vyhledat'),
							}}
						/>
					</FormWidthElementLayout>

					<Button
						onClick={() => setControls({ displayFilter: true })}
						color="primary"
						type="outlined"
						size="m"
						aria-label={t('akce.wcag.zobrazit-filtr')}>
						<GovIcon
							name="funnel"
							type="templates"
						/>
					</Button>
				</HeaderLayout>

				<Dialog
					isOpen={controls.displayFilter}
					onClose={() => setControls({ displayFilter: false })}
					title={<h3>{t('filtr.nazev')}</h3>}
					footer={
						<>
							{filter.platnostOd || filter.platnostDo || filter.text ? (
								<Button
									onClick={() => resetFilter()}
									color="primary"
									type="outlined"
									size="m"
									aria-label={t('akce.wcag.zrusit-filtr')}
									isExpandedMobile>
									{t('akce.zrusit-filtr')}
								</Button>
							) : null}
							<Button
								color="primary"
								type="solid"
								size="m"
								nativeType="submit"
								aria-label={t('akce.wcag.filtrovat')}
								isExpandedMobile>
								{t('akce.filtrovat')}
							</Button>
						</>
					}
					accessibleCloseLabel={t('akce.wcag.zavrit-filtr')}>
					<LayoutGap gap="m">
						<DSGovFlex gap="l">
							<FormWidthElementLayout>
								<FormDatePicker
									field={{
										name: 'platnostOd',
									}}
									label={{
										children: t('filtr.pole.platnost-od'),
									}}
								/>
							</FormWidthElementLayout>
							<FormWidthElementLayout>
								<FormDatePicker
									field={{
										name: 'platnostDo',
									}}
									label={{
										children: t('filtr.pole.platnost-do'),
									}}
								/>
							</FormWidthElementLayout>
						</DSGovFlex>
					</LayoutGap>
				</Dialog>
			</>
		</GovFormWeb>
	);
};
